import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";

const SeasonHistoryContext = createContext();

export const useSeasonHistory = () => {
  const context = useContext(SeasonHistoryContext);
  if (!context) {
    throw new Error(
      "useSeasonHistory must be used within a SeasonHistoryProvider"
    );
  }
  return context;
};

export const SeasonHistoryProvider = ({ children }) => {
  const [seasonHistory, setSeasonHistory] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    currentPage: 1,
    totalPages: 1,
  });

  const queryString = buildQueryString({
    page: filterOptions.currentPage,
    include: "agent,season",
  });

  const fetchSeasonHistory = useCallback(async () => {
    setSeasonHistory((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`/season-history${queryString}`);
      if (response.success) {
        setSeasonHistory({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.pagination.last_page,
        }));
      }
    } catch (error) {
      setSeasonHistory({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [queryString]);

  useEffect(() => {
    fetchSeasonHistory();
  }, [fetchSeasonHistory]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = seasonHistory.pagination?.last_page || 1;

  const Pagination = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  return (
    <SeasonHistoryContext.Provider
      value={{
        seasonHistory,
        refetch: fetchSeasonHistory,
        fetchSeasonHistory,
        Pagination,
      }}
    >
      {children}
    </SeasonHistoryContext.Provider>
  );
};
